/**
 * Pricing + checkout modal. 4 tiers, Razorpay checkout for paid tiers.
 * Tier state stored locally; future server-validated v1.1.
 */
import { ARAMGAH_TIERS, openCheckout, type PricingTier } from '../chassis/payment.ts';

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
    container-type: inline-size;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background: linear-gradient(180deg, oklch(13% 0.06 80 / 0.45), oklch(8% 0.03 268 / 0.65));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(22px, 5vw, 30px);
    color: oklch(94% 0.04 88);
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 14px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 14px;
  }
  .tier {
    padding: 18px;
    border-radius: 14px;
    border: 1px solid oklch(80% 0.04 80 / 0.16);
    background: oklch(7% 0.02 270 / 0.5);
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .tier.current {
    border-color: oklch(86% 0.14 82 / 0.6);
    background: oklch(13% 0.05 80 / 0.35);
  }
  .t-name {
    font-family: var(--display);
    font-weight: 500;
    font-size: 22px;
    color: oklch(94% 0.04 88);
  }
  .t-price {
    font-family: var(--display);
    font-style: italic;
    color: oklch(86% 0.14 82);
    font-size: 18px;
  }
  .t-tagline {
    font-family: var(--display);
    font-style: italic;
    color: oklch(76% 0.04 80);
    font-size: 13px;
  }
  ul.features {
    list-style: none;
    margin: 6px 0 12px;
    padding: 0;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.04em;
    color: oklch(82% 0.04 80);
  }
  ul.features li {
    padding: 3px 0;
    border-bottom: 1px dashed oklch(80% 0.04 80 / 0.1);
  }
  button {
    background: oklch(86% 0.14 82);
    color: oklch(12% 0.04 270);
    border: none;
    padding: 10px 18px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    cursor: pointer;
  }
  button.ghost {
    background: transparent;
    color: oklch(86% 0.04 80);
    border: 1px solid oklch(80% 0.04 80 / 0.25);
  }
  button[disabled] {
    background: oklch(40% 0.02 270);
    color: oklch(70% 0.04 80);
    cursor: not-allowed;
  }
  .b2b {
    margin-top: 18px;
    padding: 14px;
    border: 1px dashed oklch(80% 0.04 80 / 0.2);
    border-radius: 10px;
    font-family: var(--display);
    font-style: italic;
    color: oklch(76% 0.06 60);
    font-size: 14px;
    text-align: center;
  }
  @container (min-width: 600px) {
    .grid { grid-template-columns: repeat(3, 1fr); }
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · Pricing</div>
  <h3>Saare hafte ki neend, ek baar ka kharcha</h3>
  <div class="sub">On-device se kabhi off-device nahi hota · 30-din money back</div>
  <div class="grid" id="grid"></div>
  <div class="b2b">Sleep clinic / ayurveda practitioner? Likhein <a href="mailto:aditya@taxwallaai.com" style="color:oklch(86% 0.14 82)">aditya@taxwallaai.com</a></div>
</div>
`;

export class AramgahPaywall extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#renderGrid();
  }

  #renderGrid(): void {
    const grid = this.#shadow.getElementById('grid');
    if (!grid) return;
    const current = localStorage.getItem('aramgah:tier') ?? 'free';
    grid.innerHTML = ARAMGAH_TIERS.filter((t) => t.id !== 'b2b').map((t) => `
<div class="tier${t.id === current ? ' current' : ''}" data-id="${t.id}">
  <div class="t-name">${t.name}</div>
  <div class="t-price">${t.inr_annual === 0 ? 'free' : `₹${t.inr_annual}/saal · ₹${t.inr_monthly_eq}/mahina`}</div>
  <div class="t-tagline">${t.positioning_hi}</div>
  <ul class="features">
    ${t.features.map((f) => `<li>${f}</li>`).join('')}
  </ul>
  <button data-act="pick" data-id="${t.id}"${t.id === current ? ' disabled' : ''}>${t.id === current ? 'Current' : t.inr_annual === 0 ? 'Choose Free' : 'Subscribe'}</button>
</div>`).join('');
    grid.querySelectorAll<HTMLButtonElement>('button[data-act="pick"]').forEach((b) => {
      b.addEventListener('click', () => this.#pick(b.dataset['id'] ?? ''));
    });
  }

  async #pick(id: string): Promise<void> {
    const tier = ARAMGAH_TIERS.find((t) => t.id === id);
    if (!tier) return;
    try {
      await openCheckout(tier, (paymentId) => {
        localStorage.setItem('aramgah:tier', tier.id);
        localStorage.setItem('aramgah:tier-paid-id', paymentId);
        localStorage.setItem('aramgah:tier-paid-ts', new Date().toISOString());
        this.#renderGrid();
        alert(`${tier.name} active. Pro features unlocked.`);
      });
    } catch (e) {
      alert(`Checkout failed: ${(e as Error).message}`);
    }
  }
}

customElements.define('aramgah-paywall', AramgahPaywall);

export function currentTier(): PricingTier {
  const id = localStorage.getItem('aramgah:tier') ?? 'free';
  return ARAMGAH_TIERS.find((t) => t.id === id) ?? ARAMGAH_TIERS[0]!;
}
